import { TFunction } from 'i18next'
import { z } from 'zod'

import { MAX_COMPANY_NAME_LENGTH } from '@features/invoicingSettings/constants/maxCompanyNameLength'

import { OnboardingAccountingManagement } from '../enums/onboardingAccountingManagement'
import { OnboardingStep } from '../enums/onboardingStep'
import { OnboardingUserIntent } from '../enums/onboardingUserIntent'
import { OnboardingWorkMode } from '../enums/onboardingWorkMode'
import { OnboardingForm } from '../types/onboardingForm'

export const getOnboardingStepSchema = (step: OnboardingStep, t: TFunction): z.ZodType<OnboardingForm> => {
  const setupInvoicesStepFields = {
    company: z.object({
      city: z.string().optional(),
      legalStatus: z.string().optional(),
      name: z
        .string()
        .min(1, t('onboarding.error_message.companyName'))
        .max(
          MAX_COMPANY_NAME_LENGTH,
          t('onboarding.error_message.company_name_max_length', { count: MAX_COMPANY_NAME_LENGTH }),
        ),
      registrationNo: z.string().optional(),
      street: z.string().optional(),
      vatNo: z.string().optional(),
      zipcode: z.string().optional(),
    }),
    companyLogo: z
      .object({
        fileId: z.string(),
        url: z.string(),
      })
      .nullable()
      .optional(),
  }

  const namePhoneStepFields = {
    name: z.string().min(1, t('onboarding.error_message.name')),
    phone: z.string().min(1, t('onboarding.error_message.phone')),
  }

  const intentStepField = {
    intents: z.array(z.nativeEnum(OnboardingUserIntent)).min(1, t('onboarding.error_message.intent')),
  }

  const workStyleStepField = {
    workStyle: z.nativeEnum(OnboardingWorkMode, { message: t('onboarding.error_message.work_style') }),
  }

  const accountingManagementStepField = {
    accountingManagement: z.nativeEnum(OnboardingAccountingManagement, {
      message: t('onboarding.error_message.accounting_management'),
    }),
  }

  const isInvoices = step === OnboardingStep.Invoices
  const isNamePhone = step === OnboardingStep.NamePhone
  const isIntent = step === OnboardingStep.Intent
  const isWorkStyle = step === OnboardingStep.WorkStyle
  const isAccountingManagement = step === OnboardingStep.AccountingManagement

  return z.object({
    ...(isIntent && intentStepField),
    ...(isInvoices && setupInvoicesStepFields),
    ...(isNamePhone && namePhoneStepFields),
    ...(isWorkStyle && workStyleStepField),
    ...(isAccountingManagement && accountingManagementStepField),
  }) as z.ZodType<OnboardingForm>
}
